/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { GridSortModel } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import jwt_decode, { JwtPayload } from "jwt-decode";
import { DATEFORMATS } from "../globals/globals";

// This function decodes the given token and reads the expiration datetime
export const isJwtExpired = (token: string | null): boolean => {
  if (!token || token.length === 0) {
    return true;
  }

  const decodedToken: JwtPayload = jwt_decode(token);
  if (!decodedToken || !decodedToken.exp) {
    return true;
  }

  const remainingS = decodedToken.exp - new Date().getTime() / 1000;

  return remainingS <= 0;
};

export const isJwtExpiring = (token: string | null): boolean => {
  if (!token)
    return false;

  const decodedToken: JwtPayload = jwt_decode(token);
  if (!decodedToken || !decodedToken.exp) {
    return false;
  }

  return decodedToken.exp * 1000 - Date.now() < 30 * 1000;
}

export const getUserDetails = (token: string | null): { username: string; profileId: number } => {
  if (!token || token.length === 0) {
    return { username: "", profileId: 0 };
  }

  const decodedToken: any = jwt_decode(token);
  if (!decodedToken || !decodedToken.exp) {
    return { username: "", profileId: 0 };
  }

  const { username, profileId } = decodedToken;

  return { username, profileId };
};

export const jwtExpirationMs = (token: string | null): number => {
  if (!token || token.length === 0) {
    return 0;
  }

  const decodedToken: JwtPayload = jwt_decode(token);
  if (!decodedToken || !decodedToken.exp || decodedToken.exp < 0) {
    return 0;
  }

  return decodedToken.exp;
};

export const titleCase = (str: string) => {
  return str
    ? str
      .toLowerCase()
      .split(" ")
      .map(function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ")
    : "";
};

export const convertEpochToGMT = (milliseconds: number, format: string): string => {
  const date = new Date(milliseconds);

  const padZero = (num: number): string => (num < 10 ? '0' + num : '' + num);

  const year = date.getUTCFullYear().toString();
  const month = padZero(date.getUTCMonth() + 1);
  const day = padZero(date.getUTCDate());
  const hours = padZero(date.getUTCHours());
  const minutes = padZero(date.getUTCMinutes());
  const seconds = padZero(date.getUTCSeconds());

  const formatReplacements: { [key: string]: string } = {
    'YYYY': year,
    'MM': month,
    'DD': day,
    'HH': hours,
    'mm': minutes,
    'ss': seconds,
  };

  let formattedDate = format;
  for (const key in formatReplacements) {
    formattedDate = formattedDate.replace(key, formatReplacements[key]);
  }

  return formattedDate;
}

export const formatUtcTimestamp = (isoTimestamp?: string | null): string => {
  if (!isoTimestamp) {
    return "";
  }

  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const pad = (value: number): string => (value < 10 ? `0${value}` : `${value}`);

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export const getFutureDate = (day: number) => {
  const today = new Date(Date.now());
  const futureDateMiliSecond = today.getDate() + day;
  const futureDate = new Date(today.setDate(futureDateMiliSecond));
  return futureDate;
};

export const getPastDate = (day: number) => {
  const today = new Date(Date.now());
  const futureDateMiliSecond = today.getDate() - day;
  const futureDate = new Date(today.setDate(futureDateMiliSecond));
  return futureDate;
};

export const buildPath = (start: string, key: string[]) => {
  let path: string = start;
  for (let i = 0; i < key.length; i++) {
    path += "." + key[i];
  }
  return path;
};

export const exportInCSV = (response: any, exportName: string = "Untitled") => {
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  const fileName = `${exportName}-${dayjs(new Date()).format("DD-MMM-YY")}.csv`;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const downloadInCSV = (response: any, fileName: string = "Untitled") => {
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${fileName}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const exportInXML = (response: any, exportName: string = "Untitled") => {
  const url = window.URL.createObjectURL(new Blob([response.error?.data]));
  const link = document.createElement("a");
  link.href = url;
  const fileName = `${exportName}.xml`;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const downloadFile = (response: any, fileName: string) => {
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${fileName}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
};
export const isEqualObject = (object1: any, object2: any) => {
  const objKeys1 = Object.keys(object1);
  const objKeys2 = Object.keys(object2);

  if (objKeys1.length !== objKeys2.length) return false;

  for (var key of objKeys1) {
    const value1 = object1[key];
    const value2 = object2[key];

    const isObjects = isObject(value1) && isObject(value2);

    if ((isObjects && !isEqualObject(value1, value2)) || (!isObjects && value1 !== value2)) {
      return false;
    }
  }
  return true;
};

export const isObject = (object: any) => {
  return object != null && typeof object === "object";
};

export const handleBooleanStringValues = (val: any) => {
  if (typeof val === "string") {
    return val.toLowerCase() === "true";
  }
  if (typeof val === "boolean") {
    return val;
  }
  return val;
};

export const formatNumber = (num: any) => {
  if (Number.isInteger(num)) {
    return Math.abs(num);
  } else {
    return Math.abs(num.toFixed(2));
  }
};

export const formatPercent = (num: any) => {
  if (Number.isInteger(num)) {
    return Math.abs(num) > 100 ? 100 : Math.abs(num);
  } else {
    return Math.abs(num.toFixed(2)) > 100 ? 100 : Math.abs(num.toFixed(2));
  }
};

export const formatBytes = (bytes: number) => {
  if (bytes < 1024) {
    return `${bytes}Bytes`;
  } else if (bytes >= 1024 && bytes < 1048576) {
    return (bytes / 1024).toFixed(1) + "KB";
  } else if (bytes >= 1048576 && bytes < 1073741824) {
    return (bytes / 1048576).toFixed(1) + "MB";
  } else if (bytes >= 1073741824) {
    return (bytes / 1073741824).toFixed(1) + "GB";
  }
}

export const formatCounts = (number: number) => {
  if (number < 1000) {
    return formatNumber(number) || 0;
  } else if (number >= 1000 && number < 1000000) {
    return (number / 1000).toFixed(1) + "K";
  } else if (number >= 1000000) {
    return (number / 1000000).toFixed(1) + "M";
  }
}

export const errorMessage = (error: any) => {
  let filteredErrormessage: string = "Something went wrong!";
  if (error?.data) {
    let errorMsgForfilter = JSON.stringify(error?.data);
    if (errorMsgForfilter.includes("Valid Ranges")) {
      const splitArray = errorMsgForfilter.split("Valid Ranges");
      let longestError = splitArray[splitArray.length - 1];
      errorMsgForfilter = `Valid Ranges ${longestError}`;
      return errorMsgForfilter;
    }
    if (errorMsgForfilter.includes(":")) {
      const splitArray = errorMsgForfilter.split(":");

      let longestError = "";
      splitArray.forEach((str) => {
        if (str.length > longestError.length) {
          longestError = str;
        }
      });
      errorMsgForfilter = longestError;
      if (errorMsgForfilter?.includes(`\\""`)) {
        errorMsgForfilter = errorMsgForfilter.replaceAll(/\\""/g, "");
      }
      if (errorMsgForfilter?.includes(`\\"`)) {
        errorMsgForfilter = errorMsgForfilter.replaceAll(/\\"/g, "");
      }
    } else {
      if (error?.status === 504) {
        errorMsgForfilter = "Gateway Time-out: Something went wrong!";
      } else {
        errorMsgForfilter = error.data ? JSON.stringify(error.data) : JSON.stringify(error);
      }
    }
    filteredErrormessage = errorMsgForfilter;
  } else if (error?.error) {
    filteredErrormessage = !!error?.path ? `${error?.error} at ${error?.path}` : JSON.stringify(error?.error);
  } else if (error === "{}") {
    filteredErrormessage = "Unknown Error: Something went wrong!";
  } else {
    filteredErrormessage = JSON.stringify(error);
  }
  if(filteredErrormessage.includes("&#39;")) {
    filteredErrormessage = filteredErrormessage.replaceAll(/&#39;/g, "'");
  }
  return filteredErrormessage;
};

export const toEqualString = (value: string) => {
  return value
    ? value
      .replaceAll(/(?:\r\n|\r|\n)/g, "")
      .replaceAll(/\s+/g, "")
      .toLowerCase()
    : "";
};
export const isEqualString = (value1: string, value2: string) => {
  return value1 && value2 && toEqualString(value1) === toEqualString(value2);
};

// export const isSpecialChars = (value: string) => {
//   const pureString = toEqualString(value);
//   return specialChars(value);
// };

export const getSortBy = (sortModel: GridSortModel) => {
  if (sortModel.length > 0) {
    return Object.values(sortModel[0]).toString() as string;
  } else return "";
};

export const getMissingValues = (dataArray: Array<string>) => {
  let noOfRecords = dataArray.length;
  let max = dataArray.length === 0 ? 0 : Math.max(...dataArray.map(Number));
  if (noOfRecords === max) {
    return (max + 1).toString();
  }
  else {
    const missingIndexes = Array.from(Array(max), (v, i) => i + 1).filter((i) => !dataArray?.includes(i.toString()));
    const nextLanDeviceIndex = Math.min(...missingIndexes).toString();
    return nextLanDeviceIndex;
  }
};

export const hasWhiteSpace = (s: string) => {
  return /\s/g.test(s);
};

export const byteToGibiByte = (bytes: number | string) => {
  let dataInBytes: number = 0;
  if (typeof bytes === "string") {
    dataInBytes = Number(bytes);
  } else {
    dataInBytes = bytes;
  }
  return Number((dataInBytes / 1073741824).toFixed(2));
};

export const byteToMegaByte = (bytes: number | string) => {
  let dataInBytes: number = 0;
  if (typeof bytes === "string") {
    dataInBytes = Number(bytes);
  } else {
    dataInBytes = bytes;
  }
  return Number((dataInBytes / 1048576).toFixed(2));
};

export const convertSymbols = (input: string) => {
  if (input == null || input === undefined) {
    return input;
  }
  // Replace &gt; with >, &lt; with <, &amp; with &
  return input
    .replace(/&gt;/g, ">")
    .replace(/&lt;/g, "<")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#39;/g, "'");
};

export const requiredErrorMessage = (event: any) => {
  //this is use for onChange and onBlure Event to get required error message
  const { name, labels } = event?.target;
  const strLabel = labels[0]?.firstChild?.nodeValue || event?.target?.labels[0]?.firstChild?.data || titleCase(name);

  return `${strLabel} is required!.`;
};


export const transfomDetail = (detail: string) => {
  if (!detail) {
    return ""
  }
  else {
    let timestamp = dayjs(Number(detail?.split(',')[0])).format(DATEFORMATS.TS) || "";
    let detailVal = detail?.split(',')[1]
    if (!!timestamp && !!detailVal) {
      return `${timestamp} ${detailVal}`
    }
    else return detail
  }
}

export const sanitizeHtmlInput = (input: any) => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = input;
  return textArea.value || "";
}

// Utility: cn (classNames) for shadcn/ui primitives
// Combines class names conditionally
export function cn(...inputs: any[]) {
  return inputs.filter(Boolean).join(' ');
}

// Capitalize each word: "DEVICE LABEL" -> "Device Label"
export function capitalizeWords(str: string): string {
  return str.replace(/\w+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}
